import { useState, useRef } from 'react';
import {
  type ClassPassword,
  useUpdateClassPasswordApi,
} from 'eds-common/api/v1';
import { Tooltip, toast } from '@scholastic/mockingjay';
import { cn } from 'eds-common';
import { useGetMeApi } from 'eds-common/api/v2';
import Edit from '@/assets/icons/edit-light-icon.svg';
import Close from '@/assets/icons/close.svg';
import Check from '@/assets/icons/check-mark.svg';
import CheckSolid from '@/assets/icons/check-solid.svg';
import Resources from '@/assets/icons/resources-light-icon.svg';
import ErrorIcon from '@/assets/icons/circle-exclamation-solid.svg';

export function ClassroomPasswordInput({
  successMessage = 'Password copied to clipboard.',
  classPassword,
}: {
  classPassword: ClassPassword;
  successMessage?: string | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editPassword, setEditPassword] = useState(
    classPassword.class_password
  );
  const [editPasswordError, setEditPasswordError] = useState<string | null>(
    null
  );

  const { data: user } = useGetMeApi();

  const { mutate: updatePassword } = useUpdateClassPasswordApi({
    onError: (error) => {
      if (error.cause.errorBody?.code === 'class.password.already.exists') {
        setEditPasswordError('That password is taken. Try another.');
      } else {
        setEditPasswordError('An unexpected error occurred. Please try again.');
      }
    },
    onSuccess: () => {
      toast.success('Class password updated successfully!', {
        position: 'top-center',
      });
      setIsEditing(false);
    },
  });

  if (!user?.userId) {
    return null;
  }

  const handleSaveClick = () => {
    setEditPasswordError(null);
    const newClassPassword = editPassword.trim();

    if (newClassPassword === classPassword.class_password) {
      setIsEditing(false);
      return null;
    }
    if (newClassPassword.length === 0) {
      setEditPasswordError('A password is required.');
      return null;
    }
    if (newClassPassword.length < 6) {
      setEditPasswordError('That password is too short.');
      return null;
    }
    if (newClassPassword.length > 30) {
      setEditPasswordError('That password is too long.');
      return null;
    }

    return updatePassword({
      id: classPassword.id,
      class_password: newClassPassword,
      user_id: user.userId,
    });
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditPasswordError(null);
  };

  const handleEditClick = () => {
    setEditPassword(classPassword.class_password);
    setIsEditing(true);
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(
      classPassword.class_password.length,
      classPassword.class_password.length
    );
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      {isEditing && (
        <div className="text-xs text-neutral-700 tablet:max-w-[436px]">
          Your password must be at least 6 characters and no longer than 30
          characters.
        </div>
      )}
      <div className="flex w-full min-w-0 flex-wrap items-center gap-2">
        <div className="flex w-full min-w-[230px] flex-col gap-2 tablet:max-w-[436px] tablet:flex-1">
          <div
            className={cn(
              'w-full rounded-md p-[1px]',
              editPasswordError
                ? 'bg-[#DB0C0C]'
                : 'bg-gradient-to-b from-[#F25112] to-[#A81A8E]',
              {
                'focus-within:outline-0 focus-within:ring-2 focus-within:ring-brand-captainBlue focus-within:ring-offset-2':
                  isEditing,
              }
            )}
          >
            <div className="flex items-center rounded-[5px] bg-white">
              <input
                ref={inputRef}
                type="text"
                value={isEditing ? editPassword : classPassword.class_password}
                readOnly={!isEditing}
                onChange={(e) => setEditPassword(e.target.value)}
                aria-label="Classroom Password"
                className={cn(
                  'w-full bg-white p-2 text-base text-neutral-1100 outline-none',
                  isEditing
                    ? 'rounded-[5px] font-normal'
                    : 'rounded-l-[5px] font-semibold'
                )}
              />
              {!isEditing && (
                <button
                  aria-label="Edit Classroom Password"
                  type="button"
                  onClick={handleEditClick}
                  className="mr-[6px] flex min-h-6 min-w-6 items-center rounded-full bg-white focus-ring hover:bg-neutral-100"
                >
                  <Edit className="mx-auto size-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
          {editPasswordError && (
            <div
              role="alert"
              className="flex items-center gap-1 text-xs text-[#DB0C0C]"
            >
              <ErrorIcon className="size-3.5" aria-hidden />
              <span>{editPasswordError}</span>
            </div>
          )}
        </div>
        <div
          className={cn('ml-auto flex shrink-0', {
            'mt-[9px] self-baseline': editPasswordError,
          })}
        >
          {isEditing && (
            <div className="flex gap-6">
              <Tooltip content="Cancel">
                <button
                  type="button"
                  onClick={handleCancelClick}
                  aria-label="Cancel Edit"
                  className="flex size-6 items-center justify-center rounded-full border border-neutral-1100 bg-white focus-ring hover:bg-neutral-100"
                >
                  <Close className="size-4" aria-hidden />
                </button>
              </Tooltip>
              <Tooltip content="Save">
                <button
                  type="button"
                  onClick={handleSaveClick}
                  aria-label="Save Edit"
                  className="flex size-6 items-center justify-center rounded-full bg-neutral-1100 focus-ring hover:bg-neutral-700"
                >
                  <Check className="size-4" aria-hidden />
                </button>
              </Tooltip>
            </div>
          )}
          {!isEditing && (
            <Tooltip content="Copy password to clipboard">
              <button
                type="button"
                className={cn(
                  'flex min-w-[80px] items-center justify-center gap-2 rounded-full p-2 text-sm font-medium transition-all duration-300 focus-ring',
                  {
                    'bg-green-700 text-white hover:bg-green-800': isCopied,
                    'bg-white text-neutral-1100 hover:bg-neutral-100':
                      !isCopied,
                  }
                )}
                onClick={() => {
                  navigator.clipboard
                    .writeText(classPassword.class_password)
                    .then(() => {
                      setIsCopied(true);
                      if (successMessage) {
                        toast.success(successMessage, {
                          position: 'top-center',
                        });
                      }
                      setTimeout(() => setIsCopied(false), 4000);
                    })
                    .catch((error) => {
                      console.error('Failed to copy password:', error);
                    });
                }}
              >
                {!isCopied && <Resources className="size-3" aria-hidden />}
                <span>{isCopied ? 'Password copied' : 'Copy password'}</span>
                {isCopied && <CheckSolid className="size-3" aria-hidden />}
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
